/**
 * PreMeetWizard — five-step scheduler for an in-person meet.
 *
 * Steps:
 *   1. When & who    — date/time, optional match handle, ID-verify nudge
 *   2. Where         — manual place entry + safe-venue picker (city/category)
 *   3. Visibility    — quick toggles for the meet window
 *   4. Compliance    — explicit acknowledgements before the row is written
 *   5. Review & save — recap + persist via usePreMeetChecklists.upsert
 *
 * Uses existing hooks only:
 *   - usePreMeetChecklists (writes to pre_meet_checklists)
 *   - useSafeVenues        (reads safe_venues)
 *   - useProfileVisibility (reads/writes profile_visibility)
 *   - useTrustState        (drives compliance gating)
 *   - useTrustedCircle     (counts contacts → "Share with trusted" requires ≥1)
 *
 * Compliance prompts MUST be acknowledged before the "Save" CTA enables.
 * The acknowledgements are persisted as boolean columns on
 * pre_meet_checklists so an audit / DSAR export can show them later.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Calendar,
  MapPin,
  ShieldCheck,
  Eye,
  ListChecks,
  ChevronLeft,
  ChevronRight,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Users,
  Phone,
  Video,
  Search,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { usePreMeetChecklists, type PreMeetChecklist } from "@/lib/safety/usePreMeet";
import {
  useSafeVenues,
  SAFE_VENUE_CATEGORY_LABELS,
  type SafeVenue,
} from "@/lib/safety/useSafeVenues";
import { useProfileVisibility } from "@/lib/safety/useProfileVisibility";
import { useTrustedCircle } from "@/lib/safety/useTrustedCircle";
import { useTrustState } from "@/lib/trust/useTrustState";
import VenueCoverageMap from "@/components/safety/VenueCoverageMap";
import TrustedContactPicker from "@/components/safety/TrustedContactPicker";
import { UserPlus } from "lucide-react";
import type { CompliancePromptKey } from "@/lib/safety/venueCoverage";

type StepKey = "when" | "where" | "visibility" | "compliance" | "review";

const STEPS: { key: StepKey; label: string; icon: typeof Calendar }[] = [
  { key: "when", label: "When", icon: Calendar },
  { key: "where", label: "Where", icon: MapPin },
  { key: "visibility", label: "Visibility", icon: Eye },
  { key: "compliance", label: "Compliance", icon: ShieldCheck },
  { key: "review", label: "Review", icon: ListChecks },
];

interface DraftState {
  meetAt: string;                  // datetime-local string
  matchHandle: string;             // optional display name / @handle
  placeLabel: string;
  city: string;
  venueCategory: string;           // "" = all
  selectedVenue: SafeVenue | null;
  // Visibility (mirrored to profile_visibility on save if changed)
  hideAge: boolean;
  hideDistance: boolean;
  blurPhotos: boolean;
  // Checklist toggles
  hasIdVerified: boolean;
  hasVideoCall: boolean;
  hasSafeWord: boolean;
  hasTransport: boolean;
  shareTrusted: boolean;
  // Compliance acknowledgements
  ackPublic: boolean;
  ackEscape: boolean;
  ackEighteen: boolean;
  ackSafetyResources: boolean;
  /**
   * Conditional ack — only required (and only surfaced) when a curated
   * venue is selected whose city doesn't match the city the member
   * entered. Forces explicit consent to share with the Trusted Circle
   * before the checklist can be saved.
   */
  ackCityMismatch: boolean;
}

const blankDraft: DraftState = {
  meetAt: "",
  matchHandle: "",
  placeLabel: "",
  city: "",
  venueCategory: "",
  selectedVenue: null,
  hideAge: false,
  hideDistance: false,
  blurPhotos: false,
  hasIdVerified: false,
  hasVideoCall: false,
  hasSafeWord: false,
  hasTransport: false,
  shareTrusted: true,
  ackPublic: false,
  ackEscape: false,
  ackEighteen: false,
  ackSafetyResources: false,
  ackCityMismatch: false,
};

interface PreMeetWizardProps {
  triggerLabel?: string;
  triggerVariant?: "default" | "outline" | "ghost";
  onSaved?: () => void;
  /** When provided, the wizard opens in edit mode pre-filled from this row. */
  existing?: PreMeetChecklist | null;
  /** Controlled open state. When omitted, the wizard manages its own trigger. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Hide the built-in trigger button (use with controlled `open`). */
  hideTrigger?: boolean;
}

const PreMeetWizard = ({
  triggerLabel = "Plan a meet",
  triggerVariant = "outline",
  onSaved,
  existing = null,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}: PreMeetWizardProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = (v: boolean) => {
    if (onOpenChange) onOpenChange(v);
    else setInternalOpen(v);
  };
  const [stepIdx, setStepIdx] = useState(0);
  const [draft, setDraft] = useState<DraftState>(blankDraft);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const isEditing = !!existing;

  const { upsert } = usePreMeetChecklists();
  const { visibility, update: updateVisibility } = useProfileVisibility();
  const { contacts } = useTrustedCircle();
  const trust = useTrustState();

  const { venues, loading: venuesLoading } = useSafeVenues({
    city: draft.city.trim() || null,
  });

  const filteredVenues = useMemo(() => {
    if (!draft.venueCategory) return venues;
    return venues.filter((v) => v.category === draft.venueCategory);
  }, [venues, draft.venueCategory]);

  // Seed visibility toggles from current settings the first time the
  // wizard opens, so the member can adjust from their real baseline.
  // When editing, prefer the saved checklist's own values.
  useEffect(() => {
    if (!open) return;
    if (existing) {
      const meetAtLocal = existing.meet_at
        ? new Date(existing.meet_at).toISOString().slice(0, 16)
        : "";
      setDraft((d) => ({
        ...d,
        meetAt: meetAtLocal,
        placeLabel: existing.meet_place_label ?? "",
        hasIdVerified: existing.has_id_verified,
        hasVideoCall: existing.has_video_call_done,
        hasSafeWord: existing.has_safe_word,
        hasTransport: existing.has_transport,
        shareTrusted: existing.shared_with_trusted,
        // Compliance must always be re-acknowledged on edit.
        ackPublic: false,
        ackEscape: false,
        ackEighteen: false,
        ackSafetyResources: false,
        ackCityMismatch: false,
        // Visibility toggles seeded from profile_visibility below.
      }));
    }
    if (visibility) {
      setDraft((d) => ({
        ...d,
        hideAge: visibility.hide_age_from_strangers,
        hideDistance: visibility.hide_distance_from_strangers,
        blurPhotos: visibility.blur_photos_until_match,
      }));
    }
    // Only re-seed when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Pre-fill the ID-verified toggle from the member's actual KYC state
  // (only on a fresh draft — don't override saved edits).
  useEffect(() => {
    if (!open || existing) return;
    if (trust.kycVerified) {
      setDraft((d) => ({ ...d, hasIdVerified: true }));
    }
  }, [open, trust.kycVerified, existing]);

  const step = STEPS[stepIdx];
  const trustedCount = contacts.length;

  // -------- Trusted-Circle gate ------------------------------------
  // Sharing with the Trusted Circle is REQUIRED to save unless the
  // selected venue is a curated, same-city venue with documented
  // safety steps. "Same city" compares the venue's city to the city
  // the member entered in Step 2 (case-insensitive, trimmed).
  const venue = draft.selectedVenue;
  const draftCity = draft.city.trim().toLowerCase();
  const venueCity = (venue?.city ?? "").trim().toLowerCase();
  const venueIsCurated = !!venue;
  const venueSameCity =
    venueIsCurated && draftCity.length > 0 && venueCity === draftCity;
  const venueHasSafetyDoc =
    venueIsCurated && (venue?.why_safe ?? "").trim().length >= 10;
  const venueExemptsTrustedShare =
    venueIsCurated && venueSameCity && venueHasSafetyDoc;
  const trustedShareSatisfied =
    venueExemptsTrustedShare || (draft.shareTrusted && trustedCount > 0);

  // City mismatch = curated venue selected, member entered a city, but
  // they don't match. When true, we require an explicit ack checkbox in
  // the Compliance step before saving — the warning alone isn't enough.
  const cityMismatch =
    venueIsCurated && draftCity.length > 0 && !venueSameCity;
  const cityMismatchAckSatisfied = !cityMismatch || draft.ackCityMismatch;

  // Auto-clear the city-mismatch ack the moment the mismatch resolves
  // (member edits the city, switches venues, deselects the curated
  // venue, etc.). Prevents stale consent from carrying over.
  useEffect(() => {
    if (!cityMismatch && draft.ackCityMismatch) {
      setDraft((d) => ({ ...d, ackCityMismatch: false }));
    }
  }, [cityMismatch, draft.ackCityMismatch]);

  // -------- Validation per step (controls Next/Save enable state) --
  const stepValid: Record<StepKey, boolean> = {
    when: !!draft.meetAt,
    where: draft.placeLabel.trim().length >= 2,
    visibility: true,
    compliance:
      draft.ackPublic &&
      draft.ackEscape &&
      draft.ackEighteen &&
      draft.ackSafetyResources &&
      trustedShareSatisfied &&
      cityMismatchAckSatisfied,
    review: true,
  };
  const canAdvance = stepValid[step.key];

  // ------------------------------------------------------ Navigation
  const next = () => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1));
  const back = () => setStepIdx((i) => Math.max(0, i - 1));

  const reset = () => {
    setStepIdx(0);
    setDraft(blankDraft);
    setSaving(false);
  };

  const handleClose = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) reset();
  };

  // ----------------------------------------------------------- Save
  const handleSave = async () => {
    if (!stepValid.compliance) {
      toast.error("Acknowledge each safety item before saving.");
      return;
    }
    setSaving(true);

    // Persist any visibility deltas first (presentation-friendly:
    // visibility is enforced server-side, but mirroring it here keeps
    // the meet window and the long-lived setting in sync).
    if (visibility) {
      const deltas: Partial<typeof visibility> = {};
      if (visibility.hide_age_from_strangers !== draft.hideAge)
        deltas.hide_age_from_strangers = draft.hideAge;
      if (visibility.hide_distance_from_strangers !== draft.hideDistance)
        deltas.hide_distance_from_strangers = draft.hideDistance;
      if (visibility.blur_photos_until_match !== draft.blurPhotos)
        deltas.blur_photos_until_match = draft.blurPhotos;
      if (Object.keys(deltas).length > 0) {
        const { error } = await updateVisibility(deltas);
        if (error) {
          toast.error(`Visibility: ${error}`);
          // Continue anyway — the checklist itself is the primary record.
        }
      }
    }

    // Snapshot the gating decision so the saved row tells the full
    // story later (audit / DSAR / Trusted Circle hand-off PDF).
    const decision = {
      required: !venueExemptsTrustedShare,
      waived: venueExemptsTrustedShare,
      share_enabled: draft.shareTrusted,
      trusted_count: trustedCount,
      venue_is_curated: venueIsCurated,
      venue_same_city: venueSameCity,
      venue_has_safety_doc: venueHasSafetyDoc,
      city_mismatch_acknowledged: cityMismatch ? draft.ackCityMismatch : null,
      venue_id: draft.selectedVenue?.id ?? null,
      venue_name: draft.selectedVenue?.name ?? null,
      venue_city: draft.selectedVenue?.city ?? null,
      draft_city: draft.city.trim() ? draft.city.trim().toLowerCase() : null,
      decided_at: new Date().toISOString(),
    };

    const { error } = await upsert({
      ...(existing ? { id: existing.id } : {}),
      meet_at: draft.meetAt ? new Date(draft.meetAt).toISOString() : null,
      meet_place_label:
        draft.selectedVenue?.name ?? draft.placeLabel.trim(),
      meet_lat: draft.selectedVenue?.lat ?? null,
      meet_lng: draft.selectedVenue?.lng ?? null,
      shared_with_trusted: draft.shareTrusted,
      has_safe_word: draft.hasSafeWord,
      has_transport: draft.hasTransport,
      has_id_verified: draft.hasIdVerified,
      has_video_call_done: draft.hasVideoCall,
      status: existing?.status ?? "drafted",
      trusted_share_decision: decision,
    });
    setSaving(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(existing ? "Pre-meet checklist updated." : "Pre-meet checklist saved.");
    onSaved?.();
    handleClose(false);
  };

  // ============================================ Render helpers

  const StepWhen = (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="pmw-when">When</Label>
          <Input
            id="pmw-when"
            type="datetime-local"
            value={draft.meetAt}
            onChange={(e) => setDraft({ ...draft, meetAt: e.target.value })}
          />
        </div>
        <div>
          <Label htmlFor="pmw-handle">Match (optional)</Label>
          <Input
            id="pmw-handle"
            placeholder="@handle or display name"
            value={draft.matchHandle}
            onChange={(e) =>
              setDraft({ ...draft, matchHandle: e.target.value })
            }
            maxLength={64}
          />
        </div>
      </div>

      <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label className="flex items-center gap-2 text-sm">
            <ShieldCheck className="h-4 w-4" /> Match is ID-verified
          </Label>
          <Switch
            checked={draft.hasIdVerified}
            onCheckedChange={(v) => setDraft({ ...draft, hasIdVerified: v })}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <Label className="flex items-center gap-2 text-sm">
            <Video className="h-4 w-4" /> Video-call done first
          </Label>
          <Switch
            checked={draft.hasVideoCall}
            onCheckedChange={(v) => setDraft({ ...draft, hasVideoCall: v })}
          />
        </div>
        {!trust.kycVerified && (
          <p className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-400">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Your own ID isn't verified yet. Many safety features and most
            event RSVPs require it — verify in the ID panel above.
          </p>
        )}
      </div>
    </div>
  );

  const StepWhere = (
    <div className="space-y-4">
      <div>
        <Label htmlFor="pmw-place">Place</Label>
        <Input
          id="pmw-place"
          placeholder="Café Reverb, downtown"
          value={draft.placeLabel}
          onChange={(e) =>
            setDraft({ ...draft, placeLabel: e.target.value, selectedVenue: null })
          }
          maxLength={140}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label htmlFor="pmw-city">City (suggests safe venues)</Label>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              id="pmw-city"
              placeholder="Brooklyn"
              value={draft.city}
              className="pl-8"
              onChange={(e) => setDraft({ ...draft, city: e.target.value })}
            />
          </div>
        </div>
        <div>
          <Label>Venue type</Label>
          <Select
            value={draft.venueCategory || "all"}
            onValueChange={(v) =>
              setDraft({ ...draft, venueCategory: v === "all" ? "" : v })
            }
          >
            <SelectTrigger>
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {Object.entries(SAFE_VENUE_CATEGORY_LABELS).map(([k, label]) => (
                <SelectItem key={k} value={k}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Safe venues {draft.city && `· ${draft.city}`}
        </p>
        {venuesLoading ? (
          <p className="text-xs text-muted-foreground">Loading venues…</p>
        ) : filteredVenues.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-secondary/30 p-3 text-xs text-muted-foreground">
            No curated venues for those filters.{" "}
            {draft.city
              ? "Try a different city, or pick a category."
              : "Add a city to see suggestions."}
          </p>
        ) : (
          <ul className="grid gap-2 max-h-56 overflow-y-auto pr-1">
            {filteredVenues.slice(0, 12).map((v) => {
              const selected = draft.selectedVenue?.id === v.id;
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        selectedVenue: v,
                        placeLabel: v.name,
                      })
                    }
                    className={`w-full text-left rounded-lg border p-3 transition ${
                      selected
                        ? "border-primary bg-primary/5"
                        : "border-border bg-card hover:bg-secondary/40"
                    }`}
                    aria-pressed={selected}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-tight">
                          {v.name}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {SAFE_VENUE_CATEGORY_LABELS[v.category] ?? v.category}{" "}
                          · {v.city ?? "—"}
                        </p>
                        {v.why_safe && (
                          <p className="mt-1 text-[11px] text-muted-foreground line-clamp-2">
                            {v.why_safe}
                          </p>
                        )}
                        <VenueCoverageMap category={v.category} variant="compact" />
                      </div>
                      {selected ? (
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                      ) : (
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* City-mismatch warning: surfaces when the chosen curated venue's
          city doesn't match the city the member entered. Explains the
          Trusted Circle requirement so the gating doesn't feel arbitrary,
          and offers two one-click "Fix it" actions:
            (1) Jump to the city field (focus + scroll), or
            (2) Auto-switch the selected venue to a curated venue in the
                entered city (prefers same category; falls back to first
                available curated same-city venue). */}
      {venueIsCurated && draftCity.length > 0 && !venueSameCity && (() => {
        // Find a curated venue in the entered city. `venues` is already
        // scoped to draft.city by useSafeVenues, so any non-empty entry
        // is a same-city candidate. Prefer same-category for continuity.
        const sameCityCandidates = venues.filter(
          (v) => (v.city ?? "").trim().toLowerCase() === draftCity,
        );
        const sameCatMatch = sameCityCandidates.find(
          (v) => v.category === venue?.category,
        );
        const autoSwitchTarget = sameCatMatch ?? sameCityCandidates[0] ?? null;

        const focusCityField = () => {
          const el = document.getElementById("pmw-city") as HTMLInputElement | null;
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.focus({ preventScroll: true });
            el.select();
          }
        };

        const switchToSameCityVenue = () => {
          if (!autoSwitchTarget) return;
          setDraft({
            ...draft,
            selectedVenue: autoSwitchTarget,
            placeLabel: autoSwitchTarget.name,
          });
          toast.success(
            `Switched to ${autoSwitchTarget.name} in ${autoSwitchTarget.city ?? draft.city.trim()}`,
            {
              description:
                "This curated venue matches your city — Trusted Circle sharing is no longer required.",
            },
          );
        };

        return (
          <div
            role="alert"
            className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-[12px] text-amber-700 dark:text-amber-400 space-y-2"
          >
            <p className="flex items-start gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              City mismatch: {venue?.name} is in{" "}
              <span className="font-semibold">{venue?.city ?? "another city"}</span>,
              but you entered <span className="font-semibold">{draft.city.trim()}</span>.
            </p>
            <p className="text-muted-foreground">
              Wavlynq only auto-vouches for a venue when it's in the city you're
              actually meeting in — that's how we know the curated safety steps
              (staffed hours, public sightlines, accessible exits) apply to your
              specific plan.
            </p>
            <p className="text-muted-foreground">
              Because this venue can't be auto-vouched for here, you must{" "}
              <span className="font-semibold">share this meet with your Trusted Circle</span>{" "}
              before saving — at least one person who knows where you'll be and
              when to expect you back.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-7 px-2 text-[11px] border-amber-500/50 text-amber-800 dark:text-amber-300 hover:bg-amber-500/10"
                onClick={focusCityField}
              >
                Fix the city
              </Button>
              {autoSwitchTarget ? (
                <Button
                  type="button"
                  size="sm"
                  className="h-7 px-2 text-[11px] bg-amber-600 hover:bg-amber-600/90 text-white"
                  onClick={switchToSameCityVenue}
                  title={`Switch to ${autoSwitchTarget.name} (${
                    SAFE_VENUE_CATEGORY_LABELS[autoSwitchTarget.category] ??
                    autoSwitchTarget.category
                  }) in ${autoSwitchTarget.city ?? draft.city.trim()}`}
                >
                  Use a {draft.city.trim()} venue instead
                  {sameCatMatch ? " (same type)" : ""}
                </Button>
              ) : (
                <span className="text-[11px] text-muted-foreground self-center">
                  No curated venues in {draft.city.trim()} yet — keep
                  &ldquo;Share with Trusted Circle&rdquo; on to continue.
                </span>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );

  const StepVisibility = (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Tighten what strangers can see while you're planning this meet.
        Toggles persist to your global Profile Visibility settings.
      </p>
      <div className="rounded-xl border border-border bg-card divide-y divide-border">
        {[
          {
            key: "hideAge" as const,
            label: "Hide age until match",
            help: "Strangers can't see your age in Discovery.",
          },
          {
            key: "hideDistance" as const,
            label: "Hide distance until match",
            help: "Approximate distance only revealed after a match.",
          },
          {
            key: "blurPhotos" as const,
            label: "Blur photos until match",
            help: "Strangers see blurred photos. Matches see clear ones.",
          },
        ].map((row) => (
          <div
            key={row.key}
            className="flex items-start justify-between gap-4 px-4 py-3"
          >
            <div className="flex-1 min-w-0">
              <Label className="text-sm font-medium">{row.label}</Label>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {row.help}
              </p>
            </div>
            <Switch
              checked={draft[row.key]}
              onCheckedChange={(v) => setDraft({ ...draft, [row.key]: v })}
            />
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-secondary/40 p-3 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <Label className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4" /> Share details with Trusted Circle
            {!venueExemptsTrustedShare && (
              <span className="rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider">
                Required
              </span>
            )}
          </Label>
          <Switch
            checked={draft.shareTrusted}
            onCheckedChange={(v) => setDraft({ ...draft, shareTrusted: v })}
            disabled={!venueExemptsTrustedShare && trustedCount === 0}
          />
        </div>
        <p className="text-[11px] text-muted-foreground">
          {venueExemptsTrustedShare
            ? `Optional — you picked ${venue?.name}, a curated ${draft.city} venue with documented safety steps.`
            : trustedCount === 0
              ? "Required unless you pick a curated, same-city venue with documented safety steps."
              : `${trustedCount} contact${trustedCount === 1 ? "" : "s"} will be notified with the venue and time.`}
        </p>
        {!venueExemptsTrustedShare && trustedCount === 0 && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => setPickerOpen(true)}
            className="w-full"
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Add a trusted contact
          </Button>
        )}
        <div className="flex items-center justify-between gap-3 border-t border-border pt-2 mt-2">
          <Label className="flex items-center gap-2 text-sm">
            <Phone className="h-4 w-4" /> Safe word agreed with Trusted Circle
          </Label>
          <Switch
            checked={draft.hasSafeWord}
            onCheckedChange={(v) => setDraft({ ...draft, hasSafeWord: v })}
          />
        </div>
        <div className="flex items-center justify-between gap-3">
          <Label className="flex items-center gap-2 text-sm">
            <Calendar className="h-4 w-4" /> Transport home arranged
          </Label>
          <Switch
            checked={draft.hasTransport}
            onCheckedChange={(v) => setDraft({ ...draft, hasTransport: v })}
          />
        </div>
      </div>
    </div>
  );

  const complianceUnacked: CompliancePromptKey[] = (
    ["ackEighteen", "ackPublic", "ackEscape", "ackSafetyResources"] as const
  ).filter((k) => !draft[k]);

  const StepCompliance = (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        These confirmations help us route the right safety resources if
        something goes sideways. Acknowledge each before saving.
      </p>

      {draft.selectedVenue && (
        <VenueCoverageMap
          category={draft.selectedVenue.category}
          variant="detailed"
          unacknowledged={complianceUnacked}
        />
      )}
      {[
        {
          key: "ackEighteen" as const,
          title: "Both attendees are 18+",
          body: "You confirm both you and the person you're meeting are at least 18 years old. Wavlynq strictly prohibits underage meetings.",
        },
        {
          key: "ackPublic" as const,
          title: "Public location, public time",
          body: "First meets must take place in a public venue, during the venue's normal operating hours. Private residences are never recommended for a first meet.",
        },
        {
          key: "ackEscape" as const,
          title: "I have a clear exit plan",
          body: "You can leave at any time without depending on the other person for transport, money, or shelter. The Emergency Button stays available throughout the meet.",
        },
        {
          key: "ackSafetyResources" as const,
          title: "I know how to reach safety resources",
          body: "If something feels off, the /safety page lists local crisis lines (suicide, domestic violence, sexual assault, LGBTQ+, substance use). RAELA can route you in chat.",
        },
      ].map((item) => (
        <label
          key={item.key}
          htmlFor={`ack-${item.key}`}
          className={`flex gap-3 rounded-xl border p-3 cursor-pointer transition ${
            draft[item.key]
              ? "border-success/40 bg-success/5"
              : "border-border bg-card hover:bg-secondary/40"
          }`}
        >
          <Checkbox
            id={`ack-${item.key}`}
            checked={draft[item.key]}
            onCheckedChange={(v) =>
              setDraft({ ...draft, [item.key]: v === true })
            }
            className="mt-0.5"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">{item.title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{item.body}</p>
          </div>
        </label>
      ))}

      {/* Trusted-Circle gate banner */}
      {venueExemptsTrustedShare ? (
        <p className="flex items-start gap-2 rounded-lg border border-success/30 bg-success/5 p-2 text-[11px] text-success">
          <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          Trusted Circle sharing is optional — {venue?.name} is a curated
          venue in {venue?.city}, with documented safety steps.
        </p>
      ) : (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-700 dark:text-amber-400 space-y-1">
          <p className="flex items-start gap-2 font-medium">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            Trusted Circle sharing is required to save this checklist.
          </p>
          <ul className="ml-5 list-disc space-y-0.5 text-muted-foreground">
            <li className={venueIsCurated ? "line-through opacity-60" : ""}>
              Pick a curated safe venue (Step 2), and
            </li>
            <li className={venueSameCity ? "line-through opacity-60" : ""}>
              the venue's city must match the city you entered, and
            </li>
            <li className={venueHasSafetyDoc ? "line-through opacity-60" : ""}>
              the venue must have documented safety steps.
            </li>
          </ul>
          <p>
            Otherwise, turn on "Share with Trusted Circle" on the previous
            step{trustedCount === 0 ? " — and add at least one contact." : "."}
          </p>
          {trustedCount === 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setPickerOpen(true)}
              className="w-full mt-1 border-amber-500/40"
            >
              <UserPlus className="h-4 w-4 mr-1" />
              Add a trusted contact now
            </Button>
          )}
        </div>
      )}

      {/* Required city-mismatch acknowledgement.
          Renders only when the member picked a curated venue in a
          different city than they entered. The validation gate above
          (`cityMismatchAckSatisfied`) blocks Save until this is checked,
          so the member can't unintentionally save a meet whose curated
          safety steps don't actually apply to their plan. */}
      {cityMismatch && (
        <label
          htmlFor="ack-cityMismatch"
          className={`flex gap-3 rounded-xl border p-3 cursor-pointer transition ${
            draft.ackCityMismatch
              ? "border-success/40 bg-success/5"
              : "border-amber-500/50 bg-amber-500/10"
          }`}
        >
          <Checkbox
            id="ack-cityMismatch"
            checked={draft.ackCityMismatch}
            onCheckedChange={(v) =>
              setDraft({ ...draft, ackCityMismatch: v === true })
            }
            className="mt-0.5"
            aria-required="true"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">
              I understand the city mismatch and will share with my Trusted
              Circle <span className="text-destructive">*</span>
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {venue?.name} is in{" "}
              <span className="font-medium">
                {venue?.city ?? "another city"}
              </span>
              , but I'm meeting in{" "}
              <span className="font-medium">{draft.city.trim()}</span>. Wavlynq
              can't auto-vouch for this venue here, so I'm explicitly
              acknowledging that this checklist will be shared with at least
              one Trusted Circle contact before saving.
            </p>
          </div>
        </label>
      )}
    </div>
  );

  const StepReview = (
    <div className="space-y-3 text-sm">
      <div className="rounded-xl border border-border bg-card p-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <p className="font-medium">
            {draft.placeLabel || "Untitled venue"}
          </p>
          {draft.selectedVenue && (
            <span className="inline-flex items-center gap-1 rounded-full bg-success/10 text-success px-2 py-0.5 text-[10px] uppercase tracking-wider">
              <ShieldCheck className="h-3 w-3" /> Curated
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {draft.meetAt
            ? new Date(draft.meetAt).toLocaleString()
            : "No date set"}
          {draft.matchHandle && ` · with ${draft.matchHandle}`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {[
          { ok: draft.hasIdVerified, label: "Match ID-verified" },
          { ok: draft.hasVideoCall, label: "Video-call first" },
          { ok: draft.hasSafeWord, label: "Safe word" },
          { ok: draft.hasTransport, label: "Transport" },
          { ok: draft.shareTrusted, label: "Trusted Circle notified" },
          { ok: draft.hideAge, label: "Age hidden" },
          { ok: draft.hideDistance, label: "Distance hidden" },
          { ok: draft.blurPhotos, label: "Photos blurred" },
        ].map((item) => (
          <div
            key={item.label}
            className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${
              item.ok
                ? "border-success/40 bg-success/5 text-success"
                : "border-border text-muted-foreground"
            }`}
          >
            <CheckCircle2 className="h-3 w-3 shrink-0" />
            <span className="truncate">{item.label}</span>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-muted-foreground">
        Saving creates a "drafted" checklist. From the list below you
        can mark it sent, acknowledged, or met.
      </p>
    </div>
  );

  const stepBody: Record<StepKey, JSX.Element> = {
    when: StepWhen,
    where: StepWhere,
    visibility: StepVisibility,
    compliance: StepCompliance,
    review: StepReview,
  };

  const isLast = stepIdx === STEPS.length - 1;
  const StepIcon = step.icon;

  return (
    <>
    <Dialog open={open} onOpenChange={handleClose}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button variant={triggerVariant}>
            <Calendar className="h-4 w-4 mr-2" />
            {triggerLabel}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <StepIcon className="h-4 w-4 text-muted-foreground" />
            {isEditing ? "Edit meet" : "Plan a meet"} · {step.label}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="space-y-2">
          <Progress value={((stepIdx + 1) / STEPS.length) * 100} className="h-1.5" />
          <ol className="flex items-center justify-between text-[10px] uppercase tracking-wider">
            {STEPS.map((s, i) => (
              <li
                key={s.key}
                className={`flex items-center gap-1 ${
                  i === stepIdx
                    ? "text-foreground font-semibold"
                    : i < stepIdx
                      ? "text-success"
                      : "text-muted-foreground"
                }`}
              >
                <span
                  className={`inline-flex h-4 w-4 items-center justify-center rounded-full border text-[9px] tabular-nums ${
                    i === stepIdx
                      ? "border-foreground"
                      : i < stepIdx
                        ? "border-success bg-success/10"
                        : "border-border"
                  }`}
                >
                  {i + 1}
                </span>
                <span className="hidden sm:inline">{s.label}</span>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-2">{stepBody[step.key]}</div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={back} disabled={stepIdx === 0 || saving}>
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {isLast ? (
            <Button onClick={handleSave} disabled={saving || !stepValid.compliance}>
              {saving ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-1" />
              )}
              {isEditing ? "Save changes" : "Save checklist"}
            </Button>
          ) : (
            <Button onClick={next} disabled={!canAdvance}>
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
    <TrustedContactPicker
      open={pickerOpen}
      onOpenChange={setPickerOpen}
    />
    </>
  );
};

export default PreMeetWizard;
